/**
 * Putting images in the text (part 16b, table I): the files the writer picks, pastes or drops, one at a time through part
 * 16a's import, each image's markup written at a mark in the editor the batch began in, and what became of each file.
 * The import, the workspace's open book and editor, and the alt text for a nameless file are injected, so the logic runs
 * in tests.
 */
import { cleanAlt, imageMarkup } from '@cockatrice/core';
import type { ImageRefusal } from '@cockatrice/core';
import { createSignal } from 'solid-js';
import type { Accessor } from 'solid-js';
import type { ImportResult } from './importer.ts';

/** What the inserter writes through: the open chapter's editor (part 09's `ChapterEditor`, through the workspace's handle). */
export interface ImageTarget {
  markImage(at?: number): number;
  insertImage(mark: number, markup: string): boolean;
  releaseImage(mark: number): void;
}

/** The editor images go into now, and its book; null with no chapter open, or with the workspace paused (I12). */
export interface InsertPlace {
  readonly bookId: string;
  readonly editor: ImageTarget;
}

/** What became of one file (I6, I7, I10). */
export type InsertOutcome = { readonly kind: 'put-in' } | { readonly kind: 'refused'; readonly reason: ImageRefusal } | { readonly kind: 'full' | 'failed' | 'not-put-in' };

/** One file's name and what became of it. */
export interface InsertResult {
  readonly name: string;
  readonly outcome: InsertOutcome;
}

/** What the inserter shows: nothing, a batch under way (`done` of `total` files so far), or the last batches' results. */
export type InsertState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'working'; readonly done: number; readonly total: number; readonly name: string }
  | { readonly kind: 'finished'; readonly results: readonly InsertResult[] };

/** What the inserter needs; everything with an effect is injected. */
export interface InserterOptions {
  /** Part 16a's import of one file into book `bookId` (`importImage` with the app's worker and library). */
  readonly importFile: (bookId: string, file: File) => Promise<ImportResult>;
  /** The place images go now (I12). */
  readonly place: () => InsertPlace | null;
  /** I6: the alt text of a file with no readable name ("Image", in the interface's language). */
  readonly untitled: () => string;
}

/** A batch: its files, the editor and book it began in, and its mark there. */
interface Batch {
  readonly files: readonly File[];
  readonly bookId: string;
  readonly editor: ImageTarget;
  readonly mark: number;
}

/** I6: the extension a file name ends with, taken off for its alt text. */
const EXTENSION = /\.[^.\s]{1,5}$/u;

/** I6: the alt text a file is put in with: its name without the extension, as `cleanAlt` keeps it; `untitled` for none. */
export function altFor(name: string, untitled: string): string {
  return cleanAlt(name.replace(EXTENSION, '')) || untitled;
}

/** Table I: the images under way and what became of those before. */
export class ImageInserter {
  readonly state: Accessor<InsertState>;
  readonly #setState: (state: InsertState) => void;
  readonly #options: InserterOptions;
  /** I8, I11: batches waiting their turn, the first under way. */
  readonly #queue: Batch[] = [];
  /** What became of the files of the batches since the inserter was last idle or dismissed. */
  #results: InsertResult[] = [];
  /** The files of those batches. */
  #total = 0;

  constructor(options: InserterOptions) {
    this.#options = options;
    const [state, setState] = createSignal<InsertState>({ kind: 'idle' });
    this.state = state;
    this.#setState = setState;
  }

  /**
   * I1–I3, I12: puts `files` in at `at` (the cursor when not given) in the open chapter, after any batch under way;
   * nothing with none open or the workspace paused, or with no files.
   */
  insert(files: readonly File[], at?: number): void {
    const place = this.#options.place();
    if (place === null || files.length === 0) return;
    if (this.#queue.length === 0) {
      this.#results = [];
      this.#total = 0;
    }
    this.#total += files.length;
    this.#queue.push({ files, bookId: place.bookId, editor: place.editor, mark: place.editor.markImage(at) });
    if (this.#queue.length === 1) void this.#drain();
  }

  /** Clears what the last batches said; nothing while one is under way. */
  dismiss(): void {
    if (this.#queue.length === 0) this.#setState({ kind: 'idle' });
  }

  /** I8: the batches one after another, each file in its order. */
  async #drain(): Promise<void> {
    for (let batch = this.#queue[0]; batch !== undefined; batch = this.#queue[0]) {
      for (const file of batch.files) {
        this.#setState({ kind: 'working', done: this.#results.length, total: this.#total, name: file.name });
        // Nothing a file does stops the files after it.
        const outcome = await this.#one(batch, file).catch((): InsertOutcome => ({ kind: 'failed' }));
        this.#results.push({ name: file.name, outcome });
      }
      batch.editor.releaseImage(batch.mark);
      this.#queue.shift();
    }
    this.#setState({ kind: 'finished', results: this.#results });
  }

  /** I6, I7, I10, I14: one file imported and, when it is an image of the book, written at the batch's mark. */
  async #one(batch: Batch, file: File): Promise<InsertOutcome> {
    if (!this.#here(batch)) return { kind: 'not-put-in' };
    const result = await this.#options.importFile(batch.bookId, file);
    if (result.kind === 'refused') return { kind: 'refused', reason: result.reason };
    if (result.kind === 'full' || result.kind === 'failed') return { kind: result.kind };
    const markup = imageMarkup(altFor(file.name, this.#options.untitled()), result.reference);
    return this.#here(batch) && batch.editor.insertImage(batch.mark, markup) ? { kind: 'put-in' } : { kind: 'not-put-in' };
  }

  /** I10, I14: whether the batch's editor and book are still the ones images go into. */
  #here(batch: Batch): boolean {
    const place = this.#options.place();
    return place !== null && place.editor === batch.editor && place.bookId === batch.bookId;
  }
}
